export type CollectFieldType = "email" | "text" | "number" | "phone" | "selection";

export interface CollectField {
  field: string;
  type: CollectFieldType;
  prompt: string;
}

export type HandoffDestination = "webhook" | "email" | "slack" | "dashboard";

export interface HandoffConfig {
  destination: HandoffDestination;
  url?: string;      // webhook / slack
  to?: string;       // email
  include: string[]; // field names + optional "conversation_summary"
  message: string;   // farewell message shown to user
}

export interface Trigger {
  id: string;
  intent: string;                      // e.g. "needs_human"
  intent_template: string;             // predefined template key or "custom"
  description: string;                 // user-authored description
  classifier_description?: string;     // LLM-enhanced description used by classifyIntent
  collect: CollectField[];
  handoff: HandoffConfig;
  /**
   * When true, render this trigger as a clickable chip in the chat UI.
   * Clicking fires the workflow directly — no classifier inference — which
   * is both more discoverable for users and more reliable (zero false
   * negatives). Label is shown on the chip; falls back to intent name.
   */
  suggest_in_chat?: boolean;
  suggest_label?: string;              // <= 28 chars, customer-set
}

/** Compact public shape returned to the chat UI for suggested triggers. */
export interface SuggestedTrigger {
  trigger_id: string;
  label: string;
}

export const SUGGEST_LABEL_MAX_CHARS = 28;
export const SUGGEST_TRIGGERS_PER_BOT_MAX = 4;

export interface WorkflowConfig {
  enabled: boolean;
  triggers: Trigger[];
}

/**
 * Compact record of a completed handoff, kept on the session so post-dispatch
 * turns can give the chat model context. Without this, a user who asks "why
 * did you need my info?" after a handoff gets a character-breaking apology
 * ("I was following a script") because the normal RAG path has no memory of
 * what just happened.
 */
export interface LastHandoffRecord {
  trigger_intent: string;
  variables: Record<string, string>;
  farewell_message: string;
  at: string; // ISO timestamp
}

export interface WorkflowSessionState {
  session_id: string;
  active_trigger_id: string | null;
  trigger_turns: number;
  variables: Record<string, string>;
  last_handoff?: LastHandoffRecord;
}
